/**
 * Copyright (c) 2020 General Electric Company. All rights reserved.
 *
 * @class DesktopLauncher
 * @description This class is used to launch LineShaft and viewer by StudyList and WFM/MDT
 *
 * @param {boolean} useLineShaft: true for WFM and MDT; false for StudyList
 * @param {boolean} isWebSocketUsed: true if WebSocket is used; false if ActiveX is Used
 * @param {string} dataFormat: JSON or XML, used by WFM
 */

function DesktopLauncher(useLineShaft) {
  this.useLineShaft = useLineShaft;
  this.isWebSocketUsed = true;
  // Initialize uvUpdaterSocketPort to 0 for URL Viewer launch, this triggers a new
  // random port number to be generated.
  this.uvUpdaterSocketPort = 0;
  this.lineShaftSocketPort = 20000;
  this.uvUpdaterWebSocket = null;
  this.lineShaftWebSocket = null;
  this.isSSLUsed = false;
  this.userSessionId = "";
  this.uvIP = document.domain;
  this.uvPort = 1;
  this.firstTimeConnection = true;
  this.connectRetryCounter = 0;
  this.securityLevel = "0";
  this.ownerInstance = "";
  this.dataFormat = "JSON";
  this.LineshaftResponseCallback = null;
  this.ResponseCallback = null;
  this.pluginVersion = "";
  this.updaterConnected = false;
  this.lineshaftConnected = false;
  this.timeoutInSec = 300;
  this.selfUpdateRetryCounter = 1;
  this.selfUpdateTimeoutID = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
  this.ActiveXClassid = "";
  this.ActiveXCodebase = "";
  this.LaunchURIMethodUsed = true;
  this.UVUpdateInstallationInProgress = false;
  this.contextpluginVersion = "";

  // set some predefined port number to citrixTestPort using setCitrixTestPort function to test port increment logic for UVUpdater
  this.citrixTestPort = 0;

  this.m_tActiveXCabFileTimerID = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
  this.m_tUVUpdaterInstallTimerID = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];

  //Constants
  this.Const_WSSlocalhostURL = "wss://127.0.0.1:";
  this.Const_WSlocalhostURL = "ws://127.0.0.1:";
  this.Const_SessionID = "/sessionId=";
  this.Const_UVUpdaterSetupName = "UVUpdaterSetup.msi";
  this.Const_UVUpdaterExeName = "UVUpdater.exe";
  this.Const_UVUpdaterName = "UVUpdater";

  this.m_iActiveXCabFileInstallAttempts = 7;
  this.m_iUVUpdaterInstallAttempts = 11;
  this.m_SelfUpdateConnectAttempts = 20;
  this.m_iReConnectIncrementPortTimerAttempts = 4;
  this.m_bIsURLLaunch = false;
  this.m_bISIE = this.isIE();

  // set embeddedWL is used or not
  this.m_bEmbeddedWLUsed = "false";
  this.m_bIncrementIEPort = false;
}

DesktopLauncher.prototype.setEmbeddedWLUsed = function (isEmbeddedWLUsed) {
  this.m_bEmbeddedWLUsed = isEmbeddedWLUsed;
}

DesktopLauncher.prototype.setActiveXClassID = function (sActiveXClassid) {
  this.ActiveXClassid = sActiveXClassid;
}

DesktopLauncher.prototype.setActiveXCodebase = function (sActiveXCodebase) {
  this.ActiveXCodebase = sActiveXCodebase;
}

DesktopLauncher.prototype.setWebSocketUsed = function (isWebSocketUsed) {
  this.isWebSocketUsed = isWebSocketUsed;
}

/**
 * @method setUVServerIP
 * @description Setter function for user to pass into this class uvserver IP, which is
 *              sent to UVUpdater via SetServer in the uvUpdaterOnOpen event listener.
 *
 * @param {string} uvIP: the uvIP provided by user of this class.
 * @return None
 */
DesktopLauncher.prototype.setUVServerIP = function (uvIP) {
  this.uvIP = uvIP;
}

/**
 * @method setUVServerPort
 * @description Setter function for user to pass into this class the uvPort, which is
 *              sent to UVUpdater via SetUVPort in the uvUpdaterOnOpen event listener.
 *
 * @param {Number} uvPort: the uvPort provided by user of this class.
 * @return None
 */
DesktopLauncher.prototype.setUVServerPort = function (uvPort) {
  this.uvPort = uvPort;
}

DesktopLauncher.prototype.setUserSessionId = function (userSessionId) {
  this.userSessionId = userSessionId;
}

DesktopLauncher.prototype.setSecurityLevel = function (securityLevel) {
  this.securityLevel = securityLevel;
}

DesktopLauncher.prototype.setUVUpdaterSocketPort = function (uvUpdaterSocketPort) {
  this.uvUpdaterSocketPort = uvUpdaterSocketPort;
}

DesktopLauncher.prototype.setURLLaunch = function (bURLLaunch) {
  this.m_bIsURLLaunch = bURLLaunch;
}

/**
 * @method setLineshaftCallbackFunc
 * @description Setter function for user to pass in a callback function. LineShaft sends Notify
 *              response back, once event listener lineShaftOnMessage gets this response,
 *              it invokes the callback function with XML data as parameter.
 *
 * @param {function object} callbackFunc: name of the callback function provided by
 *                          the user of this class.
 * @return None
 */
DesktopLauncher.prototype.setLineshaftCallbackFunc = function (callbackFunc) {
  this.LineshaftResponseCallback = callbackFunc;
}

/**
 * @method setResponseCallback
 * @description: This callback will be used to send general response to client using websokets to launch UV
 * For example, once lineshaft websocket is connected we will send this response to notify clients
 * and they can then start sending requests to lineshaft
 *
 * @param {function object} callbackFunc: name of the callback function provided by
 *                          the user of this class.
 * @return None
 */
DesktopLauncher.prototype.setResponseCallback = function (callbackFunc) {
  this.ResponseCallback = callbackFunc;
}

/**
 * @method setOwnerInstance
 * @description Setter function for user to pass in OwnerInstance, which is sent
 *              to LineShaft via setLineShaftOwner in the lineShaftOnOpen event
 *              listener.
 *
 * @param {string} ownerInstance: the owner instance provided by the user of this class.
 * @return None
 */
DesktopLauncher.prototype.setOwnerInstance = function (ownerInstance) {
  this.ownerInstance = ownerInstance;
}

/**
 * @method appType
 * @description Setter function for user to pass in OwnerInstance, which is sent
 *              to LineShaft via setLineShaftOwner in the lineShaftOnOpen event
 *              listener.
 *
 * @param {string} appType: the owner instance provided by the user of this class.
 * @return None
 */
DesktopLauncher.prototype.setAppType = function (appType) {
  this.appType = appType;
}

/**
 * @method setDataFormat
 * @description Setter function for the user to pass in dataFormat, which is sent
 *              to LineShaft via SetLineShaftDataFormat in the lineShaftOnOpen
 *              event listener.
 *
 * @param {string} dataFormat: the data format provided by the user, either JSON or XML.
 * @return None
 */
DesktopLauncher.prototype.setDataFormat = function (dataFormat) {
  this.dataFormat = dataFormat;
}

/**
 * @method setPluginVersion
 * @description Setter function for WFM to pass into this class wfmPluginsVersion, which
 *              is sent to UVUpdater via setWFMPluginVersion (with WFM in function name)
 *              in the uvUpdaterOnOpen event listener.
 *
 * @param {string} pluginVersion: the wfmPluginsVersion provided by WFM.
 * @return None
 */
DesktopLauncher.prototype.setPluginVersion = function (pluginVersion) {
  this.pluginVersion = pluginVersion;
}

/**
 * @method setContextPluginVersion
 * @description Setter function for WFM to pass into this class wfmContextPluginVersion, which
 *              is sent to UVUpdater via setWFMContextPluginVersion (with WFM in function name)
 *              in the uvUpdaterOnOpen event listener.
 *
 * @param {string} contextpluginVersion: the WFMContextPlugin version provided by WFM.
 * @return None
 */
DesktopLauncher.prototype.setContextPluginVersion = function (contextpluginVersion) {
  this.contextpluginVersion = contextpluginVersion ? contextpluginVersion : "";
}

/**
 * @method setTimeoutValue
 * @description Setter function for user to pass into this class the timeout value, which is
 *              the length of time in seconds after which an inactive UVUpdater will exit.
 *
 * @param {Number} timeoutInSec: length of time in seconds.
 * @return None
 */
DesktopLauncher.prototype.setTimeoutValue = function (timeoutInSec) {
  this.timeoutInSec = timeoutInSec;
}

/**
 * @method isBrowserIE9OrBelow
 * @description Detect whether the browser used is Internet Explorer v9 or below. The userAgent
 *              property of navigator object is used. The userAgent value for IE9 is similar to
 *              "Mozilla/5.0 (compatible; MSIE 9.0; Windows NT 6.1;)"
 *
 * @param None
 * @return true if IE9 or below; false if IE10 or above, or other browsers, e.g. Chrome
 */
DesktopLauncher.prototype.isBrowserIE9OrBelow = function () {
  const agentHeader = navigator.userAgent.toLowerCase();

  if (agentHeader.indexOf('msie') != -1) {
    const detectIERegExp = /msie (\d+\.\d+);/
    if (detectIERegExp.test(agentHeader)) {
      const ieVersion = new Number(RegExp.$1);
      return (ieVersion <= 9);
    }
  }

  return false;
}


DesktopLauncher.prototype.isIE = function () {
  const agentHeader = window.navigator.userAgent;
  const msie = agentHeader.indexOf('MSIE '); //IE 10
  const trident = agentHeader.indexOf('Trident/'); //IE 11
  return (msie > 0 || trident > 0);
}



/**
 * @method switchToActiveXIfNeeded
 * @description If the site is configured to use WebSocket, but browser is detected to be IE9 or below,
 *              since IE9 or below doesn't support WebSocket, ActiveX will be used.
 *
 * @param None
 * @return None
 */
DesktopLauncher.prototype.switchToActiveXIfNeeded = function () {
  if (this.isWebSocketUsed && this.isBrowserIE9OrBelow()) {
    this.isWebSocketUsed = false;
  }
}

/**
 * @method generateRandomPortNumber
 * @description Generate a random port number which will be sent to UVUpdater via
 *              custom protocol, e.g. generateRandomPortNumber(10000, 50000) will
 *              generate a port number between 10000 and 50000 inclusively.
 *
 * @param {Number} minPort: minimum port number of the range
 * @param {Number} maxPort: maximum port number of the range
 * @return A random port number between minPort and maxPort (both included)
 */
DesktopLauncher.prototype.generateRandomPortNumber = function (minPort, maxPort) {
  const range = maxPort - minPort + 1;
  const randomValue = crypto.getRandomValues(new Uint32Array(1))[0];
  return Math.floor((randomValue / (0xFFFFFFFF + 1)) * range) + minPort;
}

/**
 * @method isWSSProtocolUsed
 * @description For IE, Firefox and Edge browser, we will use "wss" as WebSocket protocol;
 *              for other type of browser, e.g. Chrome, we will use "ws" as protocol.
 *
 *              For IE10 or below, the token "MSIE" exist inside the userAgent; starting
 *              IE11, "Trident" is present while at the same time "MSIE" is absent inside
 *              the userAgent. Firefox has token "Firefox"; Edge browser has token "Edge"
 *              in its userAgent properties.
 *
 * @param None
 * @return true for IE, Firefox or Edge browser; false otherwise.
 */
DesktopLauncher.prototype.isWSSProtocolUsed = function () {
  if (navigator.userAgent.indexOf('MSIE') != -1) {
    return true;
  }
  else if (navigator.userAgent.indexOf('Trident') != -1) {
    return true;
  }
  else if (navigator.userAgent.indexOf('Firefox') != -1) {
    return false;
  }
  else if (navigator.userAgent.indexOf('Edge') != -1) {
    return true;
  }
  else {
    return false;
  }
}

/**
 * @method connectUVUpdater
 * @description Open a WebSocket connection to UVUpdater to send and receive data.
 *              1. Generate a random port number between 10000 and 50000
 *              2. Launch custom protocol to send some parameters to UVUpdater initially
 *              3. Above 2 steps only occur once in each session. Refresh (F5) and different
 *                 searches skip these 2 steps.
 *              4. Create a UVUpdater WebSocket object
 *              5. In case of error, the re-try mechanism is inside its onerror event listener
 *
 * @param None
 * @return None
 */
DesktopLauncher.prototype.connectUVUpdater = function () {
  if (!this.firstTimeConnection) {
    return;
  }
  this.firstTimeConnection = false;

  if (!this.isWebSocketSupported()) {
    return;
  }

  let updaterUri = "";
  let needToLaunchCP = false;

  const currentSessionId = sessionStorage.getItem("userSessionId");
  // When refreshing page, if WebSocket port is 0, retrieve it from sessionStorage
  if (this.uvUpdaterSocketPort == 0) {
    const currentUpdaterPort = sessionStorage.getItem("updaterSocketPort");
    if (currentUpdaterPort != null) {
      this.uvUpdaterSocketPort = parseInt(currentUpdaterPort);
    }
  }

  if (this.uvUpdaterSocketPort == 0 && currentSessionId != this.userSessionId) {
    if (this.citrixTestPort != 0) {
      this.uvUpdaterSocketPort = this.citrixTestPort;
    }
    else {
      this.uvUpdaterSocketPort = this.generateRandomPortNumber(10000, 50000);
    }
    needToLaunchCP = true;
  }

  // Provide userSessionId in URI to validate/verify the session.
  if (this.isWSSProtocolUsed()) {
    this.isSSLUsed = true;
    updaterUri = this.Const_WSSlocalhostURL + this.uvUpdaterSocketPort + this.Const_SessionID + this.userSessionId;
  }
  else {
    this.isSSLUsed = false;
    updaterUri = this.Const_WSlocalhostURL + this.uvUpdaterSocketPort + this.Const_SessionID + this.userSessionId;
  }

  if (needToLaunchCP) {
    this.launchCustomProtocol();
  }

  sessionStorage.setItem("userSessionId", this.userSessionId);
  sessionStorage.setItem("updaterSocketPort", this.uvUpdaterSocketPort);

  if (!needToLaunchCP || !this.LaunchURIMethodUsed) {
    // For URL Launch scenario, before launching viewer, we need to check whether the launcher is
    // ready or not. This checking itself doesn't need to do actual connection, so a sentinel port
    // number of 50001 is used for this special purpose, it skips the connection here.
    if (this.uvUpdaterSocketPort <= 50000) {
      this.createUVUpdaterWebSocket(updaterUri);
    }
  }
}

/**
 * @method createUVUpdaterWebSocket
 * @description Open a WebSocket connection to UVUpdater to send and receive data
 *
 * @param {string} updaterUri: Uniform Resource Identifier to connect to UVUpdater
 * @return None
 */
DesktopLauncher.prototype.createUVUpdaterWebSocket = function (updaterUri) {
  // The event listeners uvUpdaterOnOpen, uvUpdaterOnClose, uvUpdaterOnMessage and
  // uvUpdaterOnError are all callback functions, their scope is different from
  // current class scope. So we define a new variable called eventCBScopeThis and
  // assign "this" object to it in order to connect the new variable to our current scope.
  const eventCBScopeThis = this;
  if (!this.updaterConnected) {
    if (this.uvUpdaterWebSocket) {
      delete this.uvUpdaterWebSocket;
      this.uvUpdaterWebSocket = null;
    }
    this.uvUpdaterWebSocket = new WebSocket(updaterUri);

    this.uvUpdaterWebSocket.onopen = uvUpdaterOnOpen;
    this.uvUpdaterWebSocket.onclose = uvUpdaterOnClose;
    this.uvUpdaterWebSocket.onmessage = uvUpdaterOnMessage;
    this.uvUpdaterWebSocket.onerror = uvUpdaterOnError;
  }
  // Declare following event listeners uvUpdaterOnOpen, uvUpdaterOnClose, uvUpdaterOnMessage
  // and uvUpdaterOnError inside this class function, so the user of this class doesn't have
  // to hard-code the instance name when this class is intantiated.

  /**
     * @method uvUpdaterOnOpen
     * @description The event listener is called when the WebSocket connection's readyState
     *              changes to OPEN; this indicates that the connection to UVUpdater is ready
     *              to send and receive data.
   *              1. Set updaterConnected flag to true so Exec() etc. waiting for this flag
   *                 will move on
     *              2. Clear timeouts set previously in uvUpdaterOnError
     *              3. In case of Studylist, invoke SetPortFunction to tell UVUpdater that
     *                 connection to its WebSocket is successful, this browser is ready to
     *                 receive data. Explicitly trigger "Update Viewer" to download newer version.
     *              4. In case of WFM, send multiple UV/WFM parameters to UVUpdater and update
     *                 viewer if needed. Ask UVUpdater to launch LineShaft,
     *
     * @param event
     * @return None
     */
  function uvUpdaterOnOpen(event) {
    //Hidden frame used for custom protocol launch is removed as it is not required, once connection is established.
    //UV automation on windows 7 fails as it waits for document.readystate=complete of current document and iframe becomes the
    //current document if used for launching custom protocol
    const HiddenFrame = document.getElementById("hiddenIframe");
    if (HiddenFrame != null) {
      HiddenFrame.parentNode.removeChild(HiddenFrame);
    }


    //set connection flag and clear timers
    eventCBScopeThis.updaterConnected = true;
    eventCBScopeThis.UVUpdateInstallationInProgress = false;
    eventCBScopeThis.ClearTimers();

    //Send response to client, indicating UVUpdater is connected
    if (eventCBScopeThis.ResponseCallback != null) {
      const JsonResponse = JSON.stringify({
        "Action": "Response",
        "Component": "UVUpdater",
        "Value": "WebSocketConnected"
      });
      // For WFM the response format should be consistent in all the places
      eventCBScopeThis.ResponseCallback(JSON.parse(JsonResponse));
    }
  }

  /**
     * @method uvUpdaterOnClose
     * @description The event listener is called when the WebSocket connection's readyState
     *              changes to CLOSED.
     *
     * @param event
     * @return None
     */
  function uvUpdaterOnClose(event) {
    // Event code 1006 Used to indicate that a connection was closed abnormally (that is, with no close frame being sent) when a status code is expected.
    if (event.code == 1006 && eventCBScopeThis.updaterConnected) {
      // Send UVUpdater terminated response to WFM, WFM will initiate the relaunch.
      if (eventCBScopeThis.ResponseCallback != null) {
        const JsonResponse = {
          Action: 'Response',
          Component: 'UVUpdater',
          Value: 'Terminated'
        };
        eventCBScopeThis.ResponseCallback(JsonResponse);
      }
    }
    else {
      eventCBScopeThis.updaterConnected = false;
    }
  }

  /**
   * @method uvUpdaterOnMessage
   * @description The event listener is called when a message is received from the UVUpdater
   *              WebSocket server.
   *
   *              For "Launch LineShaft" request, the JSON format of its response message is
   *              as follows.
   *              {
   *                  "Action": "Launch",
   *                  "Component": "Lineshaft",
   *                  "Result": "Port Number" or "Error"
   *              }
   *
   *              For CheckDownloadStatus request, the JSON format of its response message is
   *              as follows.
   *              {
   *                  "Action": "Get",
   *                  "Option": "WFMPluginDownloadStatus",
   *                  "Result": "<XML-string>"
   *              }
 *
 *              When UVUpdater detects a newer version, it will update itself to this newer
 *              version. When this occurs, following self-updating message is sent from
 *              UVUpdater to desktop_launcher.
 *              {
   *                  "Action": "Update",
 *                  "Component": "UVUpdater",
 *                  "Result": { "Result": "Exit", "Reason": "Updated"}
 *              }
   *
   * @param event
   * @return None
   */
  function uvUpdaterOnMessage(event) {
    const responseMsg = JSON.parse(event.data);

    if (eventCBScopeThis.m_bIsURLLaunch) {
      if (responseMsg.Action == 'Launch') {
        if (responseMsg.Component == 'Viewer') {
          if (responseMsg.Result == 'Success') {
            if (eventCBScopeThis.ResponseCallback != null) {
              eventCBScopeThis.ResponseCallback(responseMsg);
            }
          }
        }
      }
    }

    if (responseMsg.Action == 'Launch') {
      if (responseMsg.Component == 'Lineshaft') {
        const responseData = responseMsg.Result;
        if (responseData.toString().toLowerCase() != "error") {
          eventCBScopeThis.lineShaftSocketPort = responseData;
          eventCBScopeThis.connectLineShaft();
        } else {
          eventCBScopeThis.ResponseCallback(responseMsg);
        }
      }
    }
    else if (responseMsg.Action == 'Get') {
      if (responseMsg.Option == 'WFMPluginDownloadStatus') {
        const responseData = responseMsg.Result;
        if (eventCBScopeThis.LineshaftResponseCallback != null) {
          eventCBScopeThis.LineshaftResponseCallback(responseData);
        }
      }
    }
    else if (responseMsg.Action == 'Set') {
      const responseData = responseMsg.Result;
      if (responseData.toLowerCase() != "success") {
        alert("An error occurred, please contact PACS administrator or technical support.");
      }
    }
    else if (responseMsg.Action == 'Update') {
      if (responseMsg.Component == 'UVUpdater') {
        const responseData = responseMsg.Result;
        if (responseMsg.Result == 'Success') {
          eventCBScopeThis.SetUVPort(eventCBScopeThis.uvPort);
          eventCBScopeThis.SetServer(eventCBScopeThis.uvIP);

          let IntegrationPort;
          if (window.location.port === '' && window.location.protocol === 'https:') {
            IntegrationPort = 443;
          }
          else if (window.location.port === '' && window.location.protocol === 'http:') {
            IntegrationPort = 80;
          }
          else {
            IntegrationPort = parseInt(window.location.port);
          }
          eventCBScopeThis.SetWFMPort(IntegrationPort);
          eventCBScopeThis.SetWFMIP(window.location.hostname);
          eventCBScopeThis.SetWFMProtocol(window.location.protocol);
          eventCBScopeThis.SetWFMPluginVersion(eventCBScopeThis.pluginVersion);
          if (eventCBScopeThis.contextpluginVersion != '') {
            eventCBScopeThis.SetWFMContextPluginVersion(eventCBScopeThis.contextpluginVersion);
          }
          eventCBScopeThis.CheckforUpdate();
        } else if (responseData.Result == 'Exit' && responseData.Reason == 'Updated') {
          eventCBScopeThis.handleSelfUpdatingMsg();
        }
      } else if (responseMsg.Component == 'Viewer') {
        // Launch LineShaft on getting Viewer update response
        eventCBScopeThis.launchLineShaft();
        const responseData = responseMsg.Result;
        responseMsg.Result = responseData.Result || responseMsg.Result;
        responseMsg.LineShaft = responseData.LineShaft;
        if (responseMsg.Result != 'Success' || responseMsg.LineShaft == 'Terminated') {
          // WFM needs the viewer update error response to display notification
          eventCBScopeThis.ResponseCallback(responseMsg);
        }
      }

    }
    else if (responseMsg.Action == 'Exit') {
      if (responseMsg.Result == 'Exit') {
        if (eventCBScopeThis.ResponseCallback != null) {
          eventCBScopeThis.reset();
          eventCBScopeThis.ResponseCallback(responseMsg.Result);
        }
      }
    }
  }

  /**
   * @method uvUpdaterOnError
   * @description The event listener is called when an error occurs, e.g., if WebSocket server
   *              isn't yet running, this function will be invoked.
   *
   *              We try to reconnect to WebSocket server up to 4 times. The first time uses
   *              the port previously randomly generated, for remaining three times the port
   *              number is incremented by one.
   * @param event
   * @return None
   */
  function uvUpdaterOnError(event) {
    if (!eventCBScopeThis.updaterConnected && !eventCBScopeThis.UVUpdateInstallationInProgress) {
      // In the case of IE we need to increment the port only after 4 retries.
      // Resetting the counter to continue the retry logic 3 more times with different port number
      if (eventCBScopeThis.m_bISIE && eventCBScopeThis.connectRetryCounter == 4 &&
        !eventCBScopeThis.m_bIncrementIEPort) {
        eventCBScopeThis.connectRetryCounter = 1;
        eventCBScopeThis.m_bIncrementIEPort = true;
      }
      //4 attempts here, first attempt is already made when custom protocol is launched
      //The original port from the initial launch is given one more chance, this prevents UVUpdater running ahead of Studylist/integrations for port increment logic.
      if (eventCBScopeThis.connectRetryCounter < eventCBScopeThis.m_iReConnectIncrementPortTimerAttempts) {
        updaterUri = "";
        let currentPort;
        // In IE sometimes the UVUpdater is started after some delay and in between WFM completes 2 connection
        // attempts with the original port and then increment the port number. At that time only UVUpdater gets
        // started and listening on the original port. So further connection attempts from WFM also fails due
        // to the port mismatch. To solve this in the case of IE we are retrying the connection with the same
        // port 4 more times and then only incrementing the port number and retrying 3 more times with
        // different port numbers.
        if (eventCBScopeThis.m_bISIE && !eventCBScopeThis.m_bIncrementIEPort) {
          currentPort = eventCBScopeThis.uvUpdaterSocketPort;
        } else {
          currentPort = eventCBScopeThis.uvUpdaterSocketPort + eventCBScopeThis.connectRetryCounter;
        }

        if (eventCBScopeThis.isSSLUsed) {
          updaterUri = eventCBScopeThis.Const_WSSlocalhostURL + currentPort +
            eventCBScopeThis.Const_SessionID + eventCBScopeThis.userSessionId;
        }
        else {
          updaterUri = eventCBScopeThis.Const_WSlocalhostURL + currentPort +
            eventCBScopeThis.Const_SessionID + eventCBScopeThis.userSessionId;
        }

        eventCBScopeThis.createUVUpdaterWebSocket(updaterUri);
      }

      eventCBScopeThis.connectRetryCounter += 1;

      if (eventCBScopeThis.connectRetryCounter == 5) {
        //Give one more chance with new UVUpdater launch
        //This also helps UVUpdater auto installation using ActiveX for windows7 use case
        //This also helps the refresh use case where user cancels the UVPdater download, so the next download will only be initiated if user does a refresh and after 4 failed attempts

        // The browsers which support msLaunchUri method correctly identifies the protocol is registered or not.
        // So the installation will trigger from its failure handler. Only for other browsers we need to invoke the installation from here.
        if (!navigator.msLaunchUri) {
          eventCBScopeThis.InstallAndLaunchUVUpdater();
          eventCBScopeThis.connectRetryCounter += 1;
        } else {
          // Send UVUpdater connection failure response to WFM
          if (eventCBScopeThis.ResponseCallback != null) {
            const JsonResponse = {
              Action: 'Response',
              Component: 'UVUpdater',
              Value: 'WebSocketError'
            };
            eventCBScopeThis.ResponseCallback(JsonResponse);
          }
        }
      }
    }
  }
}




/**
 * @method launchCustomProtocol
 * @description This is used to convey port number etc. parameters from browser to UVUpdater
 *              initially before WebSocket communication between them is established.
 *
 *              Here is the format of custom protocol and the data passed in:
 *              uvweb://-p=<port number>--ssl=<true|false>--sessionId=<user session id>--uvip=
 *              <UV IP>--uvport=<UV Port>
 *
 * @param None
 * @return None
 */
DesktopLauncher.prototype.launchCustomProtocol = function () {
  const eventCBScopeThis = this;
  const CustomProtocolParams = this.GetLaunchParams();

  // For Windows 8 and above with IE 10 and above
  if (navigator.msLaunchUri && eventCBScopeThis.m_bEmbeddedWLUsed != "true") {
    navigator.msLaunchUri(CustomProtocolParams, CustomProtocolRegistered, CustomProtocolNotRegistered);
  }
  else // For other scenarios, e.g. Windows 7
  {
    eventCBScopeThis.LaunchURIMethodUsed = false;

    let iframe = document.querySelector("#hiddenIframe");
    if (!iframe) {
      iframe = _createHiddenIframe(document.body, "about:blank");
    }
    try {
      iframe.contentWindow.location.href = CustomProtocolParams;
    } catch (e) {
      CustomProtocolNotRegistered();
    }
  }

  function _createHiddenIframe(target, uri) {
    const iframe = document.createElement("iframe");
    iframe.src = uri;
    iframe.id = "hiddenIframe";
    iframe.style.display = "none";
    target.appendChild(iframe);
    return iframe;
  }


  //This is callback function which gets called if custom Protocol is registered
  //This works only for windows 8+ and IE 10+
  function CustomProtocolRegistered() {
    if (eventCBScopeThis.LaunchURIMethodUsed) {
      let updaterUri;
      if (eventCBScopeThis.isWSSProtocolUsed()) {
        updaterUri = eventCBScopeThis.Const_WSSlocalhostURL + eventCBScopeThis.uvUpdaterSocketPort + eventCBScopeThis.Const_SessionID + eventCBScopeThis.userSessionId;
      }
      else {
        updaterUri = eventCBScopeThis.Const_WSlocalhostURL + eventCBScopeThis.uvUpdaterSocketPort + eventCBScopeThis.Const_SessionID + eventCBScopeThis.userSessionId;
      }

      //This callback works for windows 8 and above.
      // For URL Launch scenario, before launching viewer, we need to check whether the launcher is
      // ready or not. This checking itself doesn't need to do actual connection, so a sentinel port
      // number of 50001 is used for this special purpose, it skips the connection here.
      if (eventCBScopeThis.uvUpdaterSocketPort <= 50000) {
        eventCBScopeThis.createUVUpdaterWebSocket(updaterUri);
      }
    }
  }

  //This is callback function which gets called if custom Protocol is not registered
  //This works only for windows 8+ and IE 10+
  function CustomProtocolNotRegistered() {
    eventCBScopeThis.InstallAndLaunchUVUpdater();
  }
}

DesktopLauncher.prototype.InstallAndLaunchUVUpdater = function () {
  // Send UVUpdater install request to WFM
  if (this.ResponseCallback != null) {
    const JsonRequest = {
      Action: 'Install',
      Component: 'UVUpdater'
    };
    this.ResponseCallback(JsonRequest);
  }
}



function IsActiveXLoaded() {

  const ActiveXObj = GetMivDeployObject();
  if (ActiveXObj != null)
    return (ActiveXObj.object != null) && (ActiveXObj.readyState == 4);
  else
    return false;
  //ActiveX control object's operational status:
  // 0 - Control is un-initialized and not ready for use
  // 1 - Control is still loading
  // 2 - Control has finished loading its data
  // 3 - User can interact with control even though it is not fully loaded
  // 4 - Control is loaded and ready for use
}

function GetMivDeployObject() {
  return document.getElementById("mivDeployer");
}

/**
 * @method isWebSocketSupported
 * @description
 *
 * @param None
 * @return true if the WebSocket is supported by the browser; false otherwise.
 */
DesktopLauncher.prototype.isWebSocketSupported = function () {
  if ("WebSocket" in window) {
    return true;
  }
  else {
    alert("WebSocket is NOT supported by your Browser!" +
      "\nPlease use a proper browser or contact the Site administrator.");
    return false;
  }
}

/**
 * @method connectLineShaft
 * @description Open a WebSocket connection to LineShaft to send and receive data.
 *
 * @param None
 * @return None
 */
DesktopLauncher.prototype.connectLineShaft = function () {
  // The event listeners lineShaftOnOpen, lineShaftOnClose, lineShaftOnMessage and
  // lineShaftOnError are all callback functions, their scope is different from
  // current class scope. So we define a new variable called eventCBScopeThis and
  // assign "this" object to it in order to connect the new variable to our current scope.
  const eventCBScopeThis = this;
  if (!this.lineshaftConnected) {
    let lineShaftUri = "";
    if (this.isSSLUsed) {
      lineShaftUri = this.Const_WSSlocalhostURL + this.lineShaftSocketPort + "/ConnectLineshaft";
    }
    else {
      lineShaftUri = this.Const_WSlocalhostURL + this.lineShaftSocketPort + "/ConnectLineshaft";
    }
    if (this.lineShaftWebSocket) {
      delete this.lineShaftWebSocket;
      this.lineShaftWebSocket = null;
    }
    this.lineShaftWebSocket = new WebSocket(lineShaftUri);

    this.lineShaftWebSocket.onopen = lineShaftOnOpen;
    this.lineShaftWebSocket.onclose = lineShaftOnClose;
    this.lineShaftWebSocket.onmessage = lineShaftOnMessage;
    this.lineShaftWebSocket.onerror = lineShaftOnError;
  }

  // Declare following event listeners lineShaftOnOpen, lineShaftOnClose, lineShaftOnMessage
  // and lineShaftOnError inside this class function, so the user of this class doesn't have
  // to hard-code the instance name when this class is intantiated.

  /**
     * @method lineShaftOnOpen
     * @description The event listener is called when the WebSocket connection's readyState
     *              changes to OPEN; this indicates that the connection to LineShaft is ready
     *              to send and receive data. We send OwnerInstance and DataFormat to LineShaft.
     *
     * @param event
     * @return None
     */
  function lineShaftOnOpen(event) {
    //Set the owner instance and data format
    eventCBScopeThis.SetLineShaftOwner(eventCBScopeThis.ownerInstance, eventCBScopeThis.appType);
    eventCBScopeThis.SetLineShaftDataFormat(eventCBScopeThis.dataFormat);
    eventCBScopeThis.lineshaftConnected = true;

    // Send LineShaft WebSocket connected response to desktop integration
    if (eventCBScopeThis.ResponseCallback != null) {
      const JsonResponse = JSON.stringify({
        "Action": "Response",
        "Component": "Lineshaft",
        "Value": "WebSocketConnected"
      });
      // For WFM the response format should be consistent in all places
      eventCBScopeThis.ResponseCallback(JSON.parse(JsonResponse));
    }
  }

  /**
   * @method lineShaftOnClose
   * @description The event listener is called when the WebSocket connection's readyState
   *              changes to CLOSED.
   *
   * @param event
   * @return None
   */
  function lineShaftOnClose(event) {
    eventCBScopeThis.lineshaftConnected = false;
    // Event code 1006 Used to indicate that a connection was closed abnormally (that is, with no close frame being sent) when a status code is expected.
    if (event.code == 1006) {
      // Send LineShaft terminated response to desktop integration and it will initiate the LineShaft restart
      if (eventCBScopeThis.ResponseCallback != null) {
        const JsonResponse = {
          Action: 'Response',
          Component: 'Lineshaft',
          Value: 'Terminated'
        };
        eventCBScopeThis.ResponseCallback(JsonResponse);
      }
    }
  }

  /**
   * @method lineShaftOnMessage
   * @description The event listener is called when a message is received from the LineShaft
   *              WebSocket server.
   *
   *              The JSON format of "Notify" response message is as follows.
   *              {
   *                  "Action": "Notify",
   *                  "Value": "<XML or JSON data>"
   *              }
   *
   * @param event
   * @return None
   */
  function lineShaftOnMessage(event) {
    const responseMsg = JSON.parse(event.data);

    if (responseMsg.Action == 'Notify') {
      const responseData = responseMsg.Value;
      if (eventCBScopeThis.LineshaftResponseCallback != null) {
        eventCBScopeThis.LineshaftResponseCallback(responseData);
      }
    }
  }

  /**
   * @method lineShaftOnError
   * @description The event listener is called when an error occurs.
   *
   * @param event
   * @return None
   */
  function lineShaftOnError(event) {

  }
}

DesktopLauncher.prototype.CheckUVUpdaterWebSocket = function () {
  if (this.uvUpdaterWebSocket != null && this.uvUpdaterWebSocket.readyState == 1) {
    return true;
  }
  else {
    return false;
  }
}

/**
 * @method Exec
 * @description The Exec interface similar to MIWDEPLOY, but under the hood
 *              WebSocket is used to send message. Here is the JSON format:
 *
 *              {
 *                  "Action": "Launch",
 *                  "Component": "Viewer",
 *                  "Parameters": "<viewer_launch_params>"
 *              }
 *
 * @param {string} param: part of JSON string to be sent to UVUpdater
 * @return None
 */
DesktopLauncher.prototype.Exec = function (param) {
  const cbScopeThis = this;
  const waitTimeInMilliSec = 500;
  const execJSONStr = JSON.stringify({
    "Action": "Launch",
    "Component": "Viewer",
    "Parameters": param
  });
  if (this.uvUpdaterWebSocket != null && this.updaterConnected) {
    this.uvUpdaterWebSocket.send(execJSONStr);
  }
  else {
    const execInterval = setInterval(function () {
      if (cbScopeThis.uvUpdaterWebSocket != null && cbScopeThis.updaterConnected) {
        cbScopeThis.uvUpdaterWebSocket.send(execJSONStr);
        clearInterval(execInterval);
      }
    }, waitTimeInMilliSec);
  }
}

/**
 * @method SetPortFunction
 * @description The SetPortFunction interface similar to MIWDEPLOY, but under
 *              the hood WebSocket is used to send message. Here is the JSON format:
 *
 *              {
 *                  "Action": "Set",
 *                  "Option": "UVPort",
 *                  "Value": <443|80>
 *              }
 *
 * @param {Number} uvControllerPort: 443 or 80
 * @param {boolean} isSSLEnabled: true if security level is 2; false otherwise.
 *                  This parameter isn't used in WebSocket case, but we keep same
 *                  interface for both ActiveX and WebSocket.
 * @return None
 */
DesktopLauncher.prototype.SetPortFunction = function (uvControllerPort, isSSLEnabled) {
  if (this.CheckUVUpdaterWebSocket()) {
    // Convert from Number to String to facilitate parsing on receiving side
    const strPort = uvControllerPort.toString();
    const setPortFunJSONStr = JSON.stringify({
      "Action": "Set",
      "Option": "UVPort",
      "Value": strPort
    });
    this.uvUpdaterWebSocket.send(setPortFunJSONStr);
  }
}

/**
 * @method BrowserUI
 * @description The BrowserUI interface similar to MIWDEPLOY, but under
 *              the hood WebSocket is used to send message.
 *
 * @param {string} param: part of JSON string to be sent to UVUpdater
 * @return None
 */
DesktopLauncher.prototype.BrowserUI = function (param) {
  alert("The function BrowserUI is called with parameter \"" + param + "\"");
}

/**
 * @method SetUVPort
 * @description The SetUVPort interface similar to SetPort in MIWDEPLOY, but under
 *              the hood WebSocket is used to send message. Here is the JSON format:
 *
 *              {
 *                  "Action": "Set",
 *                  "Option": "UVPort",
 *                  "Value": <the_port>
 *              }
 *
 * @param {Number} param: the uvport used by Workflow Manager
 * @return None
 */
DesktopLauncher.prototype.SetUVPort = function (param) {
  if (this.CheckUVUpdaterWebSocket()) {
    // Convert from Number to String to facilitate parsing on receiving side
    const strPort = param.toString();
    const setPortJSONStr = JSON.stringify({
      "Action": "Set",
      "Option": "UVPort",
      "Value": strPort
    });
    this.uvUpdaterWebSocket.send(setPortJSONStr);
  }
}

/**
 * @method SetServer
 * @description The SetServer interface similar to MIWDEPLOY, but under
 *              the hood WebSocket is used to send message. Here is the JSON format:
 *
 *              {
 *                  "Action": "Set",
 *                  "Option": "UVIP",
 *                  "Value": "<the_ip>"
 *              }
 *
 * @param {string} param: the uvserver ip used by Workflow Manager
 * @return None
 */
DesktopLauncher.prototype.SetServer = function (param) {
  if (this.CheckUVUpdaterWebSocket()) {
    const setServerJSONStr = JSON.stringify({
      "Action": "Set",
      "Option": "UVIP",
      "Value": param
    });
    this.uvUpdaterWebSocket.send(setServerJSONStr);
  }
}

/**
 * @method CheckforUpdate
 * @description The CheckforUpdate interface similar to MIWDEPLOY, but under
 *              the hood WebSocket is used to send message. Here is the JSON format:
 *
 *              {
 *                  "Action": "Update",
 *                  "Component": "Viewer"
 *              }
 *
 * @param None
 * @return None
 */
DesktopLauncher.prototype.CheckforUpdate = function () {
  if (this.CheckUVUpdaterWebSocket()) {
    const checkforUpdateJSONStr = JSON.stringify({
      "Action": "Update",
      "Component": "Viewer"
    });
    this.uvUpdaterWebSocket.send(checkforUpdateJSONStr);
  }
}

DesktopLauncher.prototype.CheckLSWebSocket = function () {
  if (this.lineShaftWebSocket != null) {
    return true;
  }
  else {
    alert("An error occurred, please contact PACS administrator or technical support.");
    return false;
  }
}

/**
 * @method SetLineShaftDataFormat
 * @description Use WebSocket to send data format to LineShaft from the user of this class.
 *              Below is the format used:
 *
 *              "Action": "Set",
 *              "Option": "DataFormat",
 *              "Value": "<the_data_format>, e.g. JSON or XML"
 *
 * @param {string} param: the data format (JSON or XML) sent from the user to LineShaft.
 * @return None
 */
DesktopLauncher.prototype.SetLineShaftDataFormat = function (param) {
  if (this.CheckLSWebSocket()) {
    const setFormatJSONStr = JSON.stringify({
      "Action": "Set",
      "Option": "DataFormat",
      "Value": param
    });
    this.lineShaftWebSocket.send(setFormatJSONStr);
  }
}

/**
 * @method SetLineShaftOwner
 * @description Use WebSocket to send OwnerInstance to LineShaft from the user of this class.
 *              Below is the format used:
 *
 *              "Action": "Set",
 *              "Option": "OwnerInstance",
 *              "Value": "<the_owner>"
 *
 * @param {string} param: the owner instance sent from the user to LineShaft.
 * @param {string} appType: the application type sent from the user to LineShaft.
 * @return None
 */
DesktopLauncher.prototype.SetLineShaftOwner = function (param, appType) {
  if (this.CheckLSWebSocket()) {
    const setOwnerJSONStr = JSON.stringify({
      "Action": "Set",
      "Option": "OwnerInstance",
      "Value": param,
      "AppType": appType
    });
    this.lineShaftWebSocket.send(setOwnerJSONStr);
  }
}

/**
 * @method InvokeAPI
 * @description Use WebSocket to send XML or JSON data to LineShaft from WFM. InvokeAPI
 *              has to wait until OwnerInstance and DataFormat have been sent to LineShaft.
 *              Below is the format used:
 *
 *              {
 *                  "Action": "InvokeAPI",
 *                  "Value": "<XML or JSON data>"
 *              }
 *
 * @param {string} param: Either XML or JSON data sent from WFM to LineShaft.
 * @return None
 */
DesktopLauncher.prototype.InvokeAPI = function (param) {
  // The anonymous function inside setInterval is a callback function, its
  // scope is different from current class scope. So we define a new variable
  // called cbScopeThis and assign this to it in order to connect the new
  // variable to our current scope.
  const cbScopeThis = this;
  const waitTimeInMilliSec = 500;
  const invokeAPIStr = JSON.stringify({
    "Action": "InvokeAPI",
    "Value": param
  });
  if (this.lineShaftWebSocket != null) {
    this.lineShaftWebSocket.send(invokeAPIStr);
  }
  else {
    const invokeAPIInterval = setInterval(function () {
      if (cbScopeThis.lineShaftWebSocket != null) {
        cbScopeThis.lineShaftWebSocket.send(invokeAPIStr);
        clearInterval(invokeAPIInterval);
      }
    }, waitTimeInMilliSec);
  }
}

/**
 * @method launchLineShaft
 * @description The message is sent to UVUpdater to launch LineShaft. UVUpdater sends back
 *              LineShaft WebSocket port number in its response. Here is the JSON format of
 *              of this request message.
 *
 *              {
 *                  "Action": "Launch",
 *                  "Component": "Lineshaft"
 *              }
 *
 * @param None
 * @return None
 */
DesktopLauncher.prototype.launchLineShaft = function () {
  if (this.CheckUVUpdaterWebSocket() && !this.lineshaftConnected) {
    const launchLSJSONStr = JSON.stringify({
      "Action": "Launch",
      "Component": "Lineshaft"
    });
    this.uvUpdaterWebSocket.send(launchLSJSONStr);
  }
}

/**
 * @method exitUVUpdater
 * @description The message is sent to UVUpdater to exit itself when user logs out. Here is the
 *              JSON format of this request message.
 *
 *              {
 *                  "Action": "Exit"
 *              }
 *
 * @param None
 * @return None
 */
DesktopLauncher.prototype.exitUVUpdater = function () {
  //Clear timers on log off
  this.ClearTimers();

  //send Exit message to UVUpdater
  if (this.CheckUVUpdaterWebSocket()) {
    const exitJSONStr = JSON.stringify({ "Action": "Exit" });
    this.uvUpdaterWebSocket.send(exitJSONStr);
  }
  // Reset the connection information for next time launch.
  this.reset();
  // Release memory allocated to WebSocket objects.
  this.cleanup();
}

/**
 * @method DeployAndLaunch
 * @description The DeployAndLaunch interface similar to MIWDEPLOY, but under the hood
 *              WebSocket is used to send message. Here is the JSON format:
 *
 *              {
 *                  "Action": "DeployAndLaunch",
 *                  "Component": "<application_name>",
 *                  "Parameters": {"setup_name":"<application_setup_name>","exe_file_name":"<application_exe_file_name>","exe_param":"<application_exe_launch_params>}
 *              }
 *
 * @param {string} application_name: name of the arbitrary application
 * @param {string} setup_name: name of the setup file of the arbitrary application
 * @param {string} exe_file_name: name of the executable file of the arbitrary application
 * @param {string} exe_param: parameters to be passed to the executable file of the arbitrary application
 * @return None
 * @Note - For security reason currently UVUpdater allows launching of only IDX through this DeployAndLaunch interface
 */
DesktopLauncher.prototype.DeployAndLaunch = function (application_name, setup_name, exe_file_name, exe_param) {
  const cbScopeThis = this;
  const waitTimeInMilliSec = 500;
  const deployAndLaunchJSONStr = JSON.stringify({
    "Action": "DeployAndLaunch",
    "Component": application_name,
    "Parameters": { "setup_name": setup_name, "exe_file_name": exe_file_name, "exe_param": exe_param }
  });
  if (this.uvUpdaterWebSocket != null && this.updaterConnected) {
    this.uvUpdaterWebSocket.send(deployAndLaunchJSONStr);
  }
  else {
    const deployAndLaunchInterval = setInterval(function () {
      if (cbScopeThis.uvUpdaterWebSocket != null && cbScopeThis.updaterConnected) {
        cbScopeThis.uvUpdaterWebSocket.send(deployAndLaunchJSONStr);
        clearInterval(deployAndLaunchInterval);
      }
    }, waitTimeInMilliSec);
  }
}

DesktopLauncher.prototype.mivLauncher = function () {
  this.switchToActiveXIfNeeded();

  if (this.isWebSocketUsed) {
    this.connectUVUpdater();
    return this;
  }
  else {
    return $('#mivDeployer').get(0);
  }
}

DesktopLauncher.prototype.mivLauncherTagLoaded = function () {
  const miv = this.mivLauncher();

  if (this.isWebSocketUsed) {
    // The WebSocket connection to UVUpdater is open and ready to communicate.
    // In case of connecting, the WebSocket send will wait until connection is open.
    // 0 - Connecting; 1 - Open; 2 - Closing; 3 - Closed
    return (miv != null) && (miv.uvUpdaterWebSocket != null) && (miv.uvUpdaterWebSocket.readyState == 0 || miv.uvUpdaterWebSocket.readyState == 1);
  }
  else {
    // ActiveX control object's operational status:
    // 0 - Control is un-initialized and not ready for use
    // 1 - Control is still loading
    // 2 - Control has finished loading its data
    // 3 - User can interact with control even though it is not fully loaded
    // 4 - Control is loaded and ready for use
    return (miv != null && miv.object != null) && (miv.readyState == 4);
  }
}

DesktopLauncher.prototype.mivLauncherReady = function () {
  if (this.isWebSocketUsed) {
    return this.mivLauncherTagLoaded();
  }
  else {
    return !this.mivLauncherTagLoaded() ? false : this.mivLauncher().object != null;
  }
}

DesktopLauncher.prototype.reset = function () {
  this.updaterConnected = false;
  this.firstTimeConnection = true;
  this.uvUpdaterSocketPort = 0;
  sessionStorage.removeItem("userSessionId");
  sessionStorage.removeItem("updaterSocketPort");
  this.selfUpdateRetryCounter = 1;
  this.connectRetryCounter = 0;
  this.UVUpdateInstallationInProgress = false;
  this.m_bIncrementIEPort = false;
}

DesktopLauncher.prototype.cleanup = function () {
  if (this.uvUpdaterWebSocket) {
    delete this.uvUpdaterWebSocket;
    this.uvUpdaterWebSocket = null;
  }
  if (this.lineShaftWebSocket) {
    delete this.lineShaftWebSocket;
    this.lineShaftWebSocket = null;
  }
}

DesktopLauncher.prototype.handleSelfUpdatingMsg = function () {
  const cbScopeThis = this;
  let suTimeOutVal = 0;
  const initialWaitTimeInMS = 20000;
  const recurWaitTimeInMS = 5000;

  while (this.selfUpdateRetryCounter < (this.m_SelfUpdateConnectAttempts + 1)) {
    // Wait for 20 seconds to allow UVUpdater's self-updating to complete,
    // then reconnect to same port. If reconnect fails, re-try every 5 seconds
    // up to 20 times , Total time for self update is 20sec + 20*5 = 120 seconds = 2 minutes
    suTimeOutVal = initialWaitTimeInMS + recurWaitTimeInMS * (this.selfUpdateRetryCounter - 1);

    if (this.selfUpdateRetryCounter == this.m_SelfUpdateConnectAttempts) {
      // If 10 reconnect attempts fail, re-launch UVUpdater using custom protocol on
      // a new port.
      this.selfUpdateTimeoutID[this.selfUpdateRetryCounter - 1] =
        setTimeout(function () {
          cbScopeThis.reset();
          cbScopeThis.connectUVUpdater();
        }, suTimeOutVal);
    }
    else {
      this.selfUpdateTimeoutID[this.selfUpdateRetryCounter - 1] =
        setTimeout(CreateSocket, suTimeOutVal);
    }

    this.selfUpdateRetryCounter += 1;
  }

  function CreateSocket() {
    cbScopeThis.createUVUpdaterWebSocket(cbScopeThis.GetURI());
  }
}


DesktopLauncher.prototype.GetURI = function () {
  let URI;
  if (this.isSSLUsed) {
    URI = this.Const_WSSlocalhostURL + this.uvUpdaterSocketPort +
      this.Const_SessionID + this.userSessionId;
  }
  else {
    URI = this.Const_WSlocalhostURL + this.uvUpdaterSocketPort +
      this.Const_SessionID + this.userSessionId;
  }

  return URI;
}

DesktopLauncher.prototype.WaitforActiveXInstallation = function () {
  const cbScopeThis = this;
  let TimeOutVal = 0;
  let RetryCounter = 1;

  //Wait for 3 seconds for the very first UVUpdater installation attempt if ActiveX is not installed, this 3 seconds is for ActiveX installtion time
  //If ActiveX is already installed then we won't wait for the initial 3 seconds
  let ActiveXDownloadTime = 3;

  if (cbScopeThis.m_bISIE) {
    if (IsActiveXLoaded()) {
      ActiveXDownloadTime = 0;
    }
  }

  //3 seconds * 6 attempts = 18 seconds (thats the time allowed for ActiveX cab file installation)
  while (RetryCounter < this.m_iActiveXCabFileInstallAttempts) {
    TimeOutVal = ActiveXDownloadTime + 3000 * (RetryCounter - 1);

    this.m_tActiveXCabFileTimerID[RetryCounter - 1] =
      setTimeout(InstallUVUpdater, TimeOutVal);

    RetryCounter += 1;
  }

  function InstallUVUpdater() {
    if (!cbScopeThis.UVUpdateInstallationInProgress) {
      if (cbScopeThis.m_bISIE) {
        if (IsActiveXLoaded()) {
          cbScopeThis.UVUpdateInstallationInProgress = true;

          if (cbScopeThis.securityLevel == 2) {
            GetMivDeployObject().SetPortFunction(443, true);
          }
          else {
            GetMivDeployObject().SetPortFunction(80, false);
          }

          const launchParams = cbScopeThis.GetLaunchParams();
          GetMivDeployObject().DeployAndLaunchApp(cbScopeThis.uvIP, cbScopeThis.Const_UVUpdaterName, cbScopeThis.Const_UVUpdaterSetupName,
            cbScopeThis.Const_UVUpdaterExeName, launchParams, false, false);
          ConnectToUVUpdaterAfterInstallation();
        }
      }
      else {
        //For other browsers
        ConnectToUVUpdaterAfterInstallation();
      }
    }
    else {
      cbScopeThis.ClearActiveXCabFileTimer();
    }
  }

  function ConnectToUVUpdaterAfterInstallation() {
    TimeOutVal = 0;
    let UpdateRetryCounter = 1;
    while (UpdateRetryCounter < cbScopeThis.m_iUVUpdaterInstallAttempts) {
      // Wait for 6 seconds to allow UVUpdater to install itself,
      // then reconnect to original failed  port. If reconnect fails, re-try every 3 seconds
      // up to 8 times, this is required to give time to UVUpdater for installation.

      // Total = 30 seconds (6000 + (3000 * 8)), is the total time we give for installation of UVUpdater
      // If not able to connect in 30 seconds, trying launching CP again.

      TimeOutVal = 6000 + 3000 * (UpdateRetryCounter - 1);

      if (UpdateRetryCounter == cbScopeThis.m_iUVUpdaterInstallAttempts - 1) {
        //relaunch custom protocol?
      }
      else {
        cbScopeThis.m_tUVUpdaterInstallTimerID[UpdateRetryCounter - 1] =
          setTimeout(CreateWebSocket, TimeOutVal);
      }

      UpdateRetryCounter += 1;
    }
  }

  function CreateWebSocket() {
    cbScopeThis.createUVUpdaterWebSocket(cbScopeThis.GetURI());
  }
}

DesktopLauncher.prototype.ClearActiveXCabFileTimer = function () {
  for (let i = 0; i < this.m_iActiveXCabFileInstallAttempts; i++) {
    if (this.m_tActiveXCabFileTimerID[i] != 0) {
      clearTimeout(this.m_tActiveXCabFileTimerID[i]);
    }
  }
}


DesktopLauncher.prototype.ClearUVUpdaterInstallTimer = function () {
  for (let i = 0; i < this.m_iUVUpdaterInstallAttempts; i++) {
    if (this.m_tUVUpdaterInstallTimerID[i] != 0) {
      clearTimeout(this.m_tUVUpdaterInstallTimerID[i]);
    }
  }
}

DesktopLauncher.prototype.ClearSelfUpdateTimer = function () {
  for (let i = 0; i < this.m_SelfUpdateConnectAttempts; i++) {
    if (this.selfUpdateTimeoutID[i] != 0) {
      clearTimeout(this.selfUpdateTimeoutID[i]);
    }
  }
}

DesktopLauncher.prototype.ClearTimers = function () {
  this.ClearSelfUpdateTimer();
  this.ClearActiveXCabFileTimer();
  this.ClearUVUpdaterInstallTimer();
}

DesktopLauncher.prototype.ConstructLaunchparams = function (delimeter) {
  let LaunchParams = "uvweb://" + delimeter + "p=" + this.uvUpdaterSocketPort;

  if (this.isSSLUsed) {
    LaunchParams += delimeter + delimeter + "ssl=true";
  }
  else {
    LaunchParams += delimeter + delimeter + "ssl=false";
  }

  LaunchParams += delimeter + delimeter + "sessionId=" + this.userSessionId;
  LaunchParams += delimeter + delimeter + "uvip=" + this.uvIP;

  if (!this.useLineShaft) {
    if (this.securityLevel == 2) {
      LaunchParams += delimeter + delimeter + "uvport=443";
    }
    else {
      LaunchParams += delimeter + delimeter + "uvport=80";
    }
  }
  else {
    LaunchParams += delimeter + delimeter + "uvport=" + this.uvPort;
  }

  LaunchParams += delimeter + delimeter + "timeout=" + this.timeoutInSec;

  return LaunchParams;
}

DesktopLauncher.prototype.GetLaunchParams = function () {
  let LaunchParams = "";

  if (this.uvIP.indexOf("-") == -1) {
    LaunchParams = this.ConstructLaunchparams("-");
  }
  else {

    LaunchParams = this.ConstructLaunchparams("~");
  }

  return LaunchParams;
}
DesktopLauncher.prototype.setCitrixTestPort = function (port) {
  this.citrixTestPort = port;
}

//////////////////////////////////////////////////////////////////////////
//					Worfklow Manager specific functions					//
//////////////////////////////////////////////////////////////////////////

/**
 * @method SetWFMPort
 * @description The SetWFMPort interface similar to MIWDEPLOY, but under
 *              the hood WebSocket is used to send message. Here is the JSON format:
 *
 *              {
 *                  "Action": "Set",
 *                  "Option": "WFMPort",
 *                  "Value": <the_port>
 *              }
 *
 * @param {Number} param: the port used by Workflow Manager
 * @return None
 */
DesktopLauncher.prototype.SetWFMPort = function (param) {
  if (this.CheckUVUpdaterWebSocket()) {
    // Convert from Number to String to facilitate parsing on receiving side
    const strPort = param.toString();
    const setWFMPortJSONStr = JSON.stringify({
      "Action": "Set",
      "Option": "WFMPort",
      "Value": strPort
    });
    this.uvUpdaterWebSocket.send(setWFMPortJSONStr);
  }
}

/**
 * @method SetWFMIP
 * @description The SetWFMIP interface similar to MIWDEPLOY, but under
 *              the hood WebSocket is used to send message. Here is the JSON format:
 *
 *              {
 *                  "Action": "Set",
 *                  "Option": "WFMIP",
 *                  "Value": "<the_ip>"
 *              }
 *
 * @param {string} param: the IP of Workflow Manager
 * @return None
 */
DesktopLauncher.prototype.SetWFMIP = function (param) {
  if (this.CheckUVUpdaterWebSocket()) {
    const setWFMIPJSONStr = JSON.stringify({
      "Action": "Set",
      "Option": "WFMIP",
      "Value": param
    });
    this.uvUpdaterWebSocket.send(setWFMIPJSONStr);
  }
}

/**
 * @method SetWFMProtocol
 * @description The SetWFMProtocol interface similar to MIWDEPLOY, but under
 *              the hood WebSocket is used to send message. Here is the JSON format:
 *
 *              {
 *                  "Action": "Set",
 *                  "Option": "WFMProtocol",
 *                  "Value": "<the_protocol>"
 *              }
 *
 * @param {string} param: the protocol used by Workflow Manager
 * @return None
 */
DesktopLauncher.prototype.SetWFMProtocol = function (param) {
  if (this.CheckUVUpdaterWebSocket()) {
    const setWFMProtocolJSONStr = JSON.stringify({
      "Action": "Set",
      "Option": "WFMProtocol",
      "Value": param
    });
    this.uvUpdaterWebSocket.send(setWFMProtocolJSONStr);
  }
}

/**
 * @method SetWFMPluginVersion
 * @description The SetWFMPluginVersion interface similar to MIWDEPLOY, but under
 *              the hood WebSocket is used to send message. Here is the JSON format:
 *
 *              {
 *                  "Action": "Set",
 *                  "Option": "WFMPluginVersion",
 *                  "Value": "<the_plugin_version>"
 *              }
 *
 * @param {string} param: the plugin version used by Workflow Manager
 * @return None
 */
DesktopLauncher.prototype.SetWFMPluginVersion = function (param) {
  if (this.CheckUVUpdaterWebSocket()) {
    const setWFMPluginJSONStr = JSON.stringify({
      "Action": "Set",
      "Option": "WFMPluginVersion",
      "Value": param
    });
    this.uvUpdaterWebSocket.send(setWFMPluginJSONStr);
  }
}

/**
 * @method SetWFMContextPluginVersion
 * @description Set WFMContextPlugin version. Here is the JSON format:
 *
 *              {
 *                  "Action": "Set",
 *                  "Option": "WFMContextPluginVersion",
 *                  "Value": "<the_plugin_version>"
 *              }
 *
 * @param {string} param: the plugin version used by Workflow Manager
 * @return None
 */
 DesktopLauncher.prototype.SetWFMContextPluginVersion = function (param) {
  if (this.CheckUVUpdaterWebSocket()) {
    const setWFMPluginJSONStr = JSON.stringify({
      "Action": "Set",
      "Option": "WFMContextPluginVersion",
      "Value": param
    });
    this.uvUpdaterWebSocket.send(setWFMPluginJSONStr);
  }
}

/**
 * @method CheckDownloadStatus
 * @description The message is sent to UVUpdater to check WFM Plugins download status. UVUpdater
 *              sends back a XML string in its response. Here is the JSON format of this request
 *              message.
 *
 *              {
 *                  "Action": "Get",
 *                  "Option": "WFMPluginDownloadStatus"
 *              }
 *
 * @param None
 * @return None
 */
DesktopLauncher.prototype.CheckDownloadStatus = function () {
  if (this.CheckUVUpdaterWebSocket()) {
    const checkDLJSONStr = JSON.stringify({
      "Action": "Get",
      "Option": "WFMPluginDownloadStatus"
    });
    this.uvUpdaterWebSocket.send(checkDLJSONStr);
  }
}
